import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { catchError, filter, of, switchMap, take } from 'rxjs';

import { SKIP_GBE_ERROR_NOTIFICATION } from '@core/interceptors/gbe-error.interceptor';
import { ErrorCatalogWire } from '@core/models/error-catalog.model';
import { GbeEndPointV4 } from '@core/models/gbe/gbe-endpoint.enum';
import { selectUser } from '@core/store/auth/auth.selectors';
import { GBE_BASE_URL } from '@core/tokens/gbe-url.token';

import { readAnnotations } from './error-catalog-annotations';
import { ErrorCatalogService } from './error-catalog.service';

/**
 * Loads the error catalog once per session into [`ErrorCatalogService`](ErrorCatalogService).
 *
 * Once per session rather than per dataspace because the catalog describes the DataLayer
 * build, which is why GBE serves an identical response to every caller and marks it
 * cacheable.
 *
 * The request waits for an authenticated user. `GbeTokensService` fills its synchronous
 * token cache from an OIDC observable, so a request issued while the app is still
 * bootstrapping carries no `Authorization` header at all; GBE answers 401 and
 * `gbeErrorInterceptor` turns that into a logout or a Keycloak round-trip.
 *
 * A failure is swallowed and the generic error notification suppressed: the endpoint
 * requires the caller's data-layer permission, so a 403 is an ordinary outcome for some
 * users, and annotations are an enrichment rather than a dependency. Nothing retries — a
 * session that starts without them renders findings through GFE's own translations for its
 * whole life, which is the same behaviour as before the catalog existed.
 */
@Injectable({ providedIn: 'root' })
export class ErrorCatalogLoader {
  private readonly gbeUrl = inject(GBE_BASE_URL);
  private readonly http = inject(HttpClient);
  private readonly catalog = inject(ErrorCatalogService);
  private readonly store = inject(Store);

  load(): void {
    this.store
      .select(selectUser)
      .pipe(
        filter((user) => !!user),
        take(1),
        switchMap(() =>
          this.http.get<ErrorCatalogWire>(`${this.gbeUrl}/${GbeEndPointV4.ERROR_CATALOG}`, {
            context: new HttpContext().set(SKIP_GBE_ERROR_NOTIFICATION, true)
          })
        ),
        catchError(() => of<ErrorCatalogWire>({ errors: [] }))
      )
      .subscribe((wire) => this.catalog.setAnnotations(readAnnotations(wire)));
  }
}
